/** @jsx jsx */
import { Button, Heading, Inline, jsx, Stack } from '@front10/base-elements';
import { Icon } from '../../../packages/base-elements/Icon/Icon';
import { withContainer } from '@front10/container-elements';
import { useTranslation, useIsMobileView } from '@front10/utils';
import { TabList, TabPanel, TabPanels, Tabs } from '@reach/tabs';
import type { Moment } from 'moment';
import type PubSub from 'pubsub-js';
import { Fragment, useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { getDate } from '../../../helpers/datetime';
import {
  TrackingPayload,
  useTrackingChange,
  VerticalBookingActions
} from '../../../_integrations/everymundo/tracking/booking';
import { MobileBody, MobileFilterDialog } from '../../lab/MobileFilterView';
import { useCheckboxProps, useSelectProps } from '../../lab/utils';
import { FetchAirlines } from './airlines';
import { AirportMarketFilter, FetchAirports } from './airports';
import { BusinessRules } from './bookingBusinessRules';
import { BookingComponentsConfigurations } from './bookingComponentsConfigurations';
import { BookingPanel, BookingPanelProps, BookingPanelState } from './BookingPanel';
import { bookingStyles } from './bookingStyles';
import { BookingTab } from './BookingTab';
import { PopupDialog } from './PopupDialog';
import { FetchBlackoutDates } from './blackoutDates';

interface PopupData {
  airlineCode: string;
  airline?: string;
  currencyCode: string;
  dataType: string;
  departureDate: Moment;
  destination: string;
  farenetTravelClass: string;
  flightDeltaDays: number;
  flightType: string;
  formattedDepartureDate: string;
  formattedReturnDate: string;
  formattedTotalPrice: string;
  formattedUsdTotalPrice: string;
  journeyType: string;
  origin: string;
  passengers: number;
  popularity: number;
  returnDate: Moment;
  searchDate: string;
  totalPrice: number;
  trackingParameters: Record<string, any>;
  extraInfo: Record<string, any>;
  travelClass: string;
  usdTotalPrice: number;
  hotelId?: string;
}

export interface BookingPopupProps {
  showDisclaimer: boolean;
  keyDisclaimer: string;
  onSubmit: (data: TrackingPayload) => void;
  onOriginChange?: (state: BookingPanelState) => void;
  onDestinationChange?: (state: BookingPanelState) => void;
  onJourneyTypeChange?: (state: BookingPanelState) => void;
  onDepartureDateChange?: (state: BookingPanelState) => void;
  onReturnDateChange?: (state: BookingPanelState) => void;
  onAirlineChange?: (state: BookingPanelState) => void;
  bgImage?: string;
  fetchAirports: FetchAirports;
  fetchAirlines: FetchAirlines;
  filterFields?: AirportMarketFilter;
  businessRules: BusinessRules;
  passengersRules?: Array<string>;
  selection: string;
  title: string;
  defaultVisible: boolean;
  getPubSub?: () => typeof PubSub;
  componentsConfiguration: BookingComponentsConfigurations & {
    panels:
      | Record<string, BookingPanelProps & BookingComponentsConfigurations>
      | Array<BookingPanelProps & BookingComponentsConfigurations>;
  };
  originDestinationLogic?: string;
  fetchBlackoutDates: FetchBlackoutDates;
  activePanel?: number;
  setActivePanel?: (idx: number) => void;
}

export function BookingPopup(props: BookingPopupProps) {
  const translate = useTranslation();
  const getPubSub = props.getPubSub;

  const businessRules = props.businessRules;
  const dateFormatHint = props.componentsConfiguration.dateSelector?.dateFormat;
  const isMobile = useIsMobileView();
  const [popupData, setPopupData] = useState<Partial<PopupData>>({});
  const [currentPopupData, setCurrentPopupData] = useState<BookingPanelState>();

  const journeyTypes = useSelectProps(businessRules.journeyTypes, popupData.journeyType);
  const prepopulatedAirlines = popupData.airline;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const height = isMobile ? '100%' : undefined;
  const preventBottomDisplacement =
    props.componentsConfiguration?.preventBottomDisplacement === true ? true : false;
  const preventScrollOnPopupOpen =
    props.componentsConfiguration?.preventScrollOnPopupOpen === true ? true : false;

  const configConfiguration = useMemo(
    () => ({
      ...props.componentsConfiguration,
      dateSelector: {
        shouldFlip: true,
        ...props.componentsConfiguration.dateSelector
      }
    }),
    [props.componentsConfiguration]
  );

  useEffect(() => {
    buttonRef?.current?.focus();
  }, []);

  const dates = useMemo(() => {
    return {
      start: getDate(popupData.departureDate, dateFormatHint),
      end: getDate(popupData.returnDate, dateFormatHint),
      startTime: businessRules.timePickers?.defaultValue,
      endTime: businessRules.timePickers?.defaultValue
    };
  }, [dateFormatHint, popupData, businessRules.timePickers?.defaultValue]);

  const carTypes = useSelectProps(businessRules.carTypes);
  const dropOff = useSelectProps(businessRules.dropOff);
  const hotelCategories = useSelectProps(businessRules.hotelCategories);
  const driverAges = useSelectProps(businessRules.driverAges);
  const nationalities = useSelectProps(businessRules.nationalities);

  const additionalHotelRules = useCheckboxProps(businessRules.additionalHotelRules);
  const additionalFlightRules = useCheckboxProps(businessRules.additionalFlightRules);
  const additionalCarRules = useCheckboxProps(businessRules.additionalCarRules);

  const [isVisible, setVisible] = useState(props.defaultVisible ?? false);
  function disableScrolling() {
    const x = window.scrollX;
    const y = window.scrollY;
    window.onscroll = function () {
      window.scrollTo(x, y);
    };
  }

  function enableScrolling() {
    window.onscroll = function () {};
  }
  const open = useCallback(() => {
    if (preventScrollOnPopupOpen) {
      disableScrolling();
    }
    setVisible(true);
  }, [preventScrollOnPopupOpen]);
  const track = useTrackingChange();
  const close = useCallback(
    (currentPopupData) => {
      if (preventScrollOnPopupOpen) {
        enableScrolling();
      }
      setVisible(false);
      track(VerticalBookingActions.POPUP_CLOSE, currentPopupData);
    },
    [preventScrollOnPopupOpen, track]
  );
  const panels = useMemo(() => {
    if (Array.isArray(props.componentsConfiguration.panels)) {
      return props.componentsConfiguration.panels;
    }
    return Object.values(props.componentsConfiguration.panels ?? {});
  }, [props.componentsConfiguration.panels]);

  useEffect(() => {
    const pubSub = getPubSub?.();
    let token = pubSub?.subscribe('em.booking.open', (_event: string, data: PopupData) => {
      setPopupData(data);

      const newdata: BookingPanelState = {
        type: typeof panels[0].type === 'string' ? panels[0].type : panels[0].type.defaultValue,
        flightType: data.journeyType,
        travelClass: data.travelClass,
        passengerInfo: {
          count: [data.passengers],
          ages: [[]]
        },
        origin: { label: data.origin, value: data.origin },
        destination: { label: data.destination, value: data.destination },
        dates: {
          start: getDate(data.departureDate),
          end: getDate(data.returnDate)
        }
      };

      track(VerticalBookingActions.POPUP_OPENED, newdata);
      open();
    });

    return () => {
      pubSub?.unsubscribe(token);
    };
  }, [
    getPubSub,
    open,
    journeyTypes,
    panels,
    track,
    props.componentsConfiguration.flexibleDatesSelector
  ]);

  const disclaimer = props.componentsConfiguration.disclaimer;

  const receivedOnSubmit = props.onSubmit;
  const onSubmit = useCallback(
    (data: BookingPanelState) =>
      receivedOnSubmit({
        ...data,
        extraInfo: popupData.extraInfo,
        airline: popupData.airline,
        trackingParameters: popupData.trackingParameters,
        hotelId: popupData.hotelId
      }),
    [
      popupData.airline,
      popupData.extraInfo,
      popupData.hotelId,
      popupData.trackingParameters,
      receivedOnSubmit
    ]
  );

  if (!isVisible) {
    return null;
  }

  const isOnePanel = panels.length === 1;
  const PanelComponent = isOnePanel ? Fragment : TabPanel;

  const panelsComponent = panels.map((panel) => {
    let rules;
    if (panel.type === 'flight') {
      rules = additionalFlightRules;
    } else if (panel.type === 'car') {
      rules = additionalCarRules;
    } else if (panel.type === 'hotel') {
      rules = additionalHotelRules;
    }

    const airlinesSelector = {
      airlinesList: props.componentsConfiguration.airlinesSelector?.airlinesList ?? [],
      enabled:
        (props.componentsConfiguration.airlinesSelector?.enabled || panel.enableAirlineFilter) ??
        false
    };

    return (
      <PanelComponent
        sx={{ height: height }}
        key={panel.name}
        {...(!isOnePanel && { tabIndex: -1 })}
      >
        <BookingPanel
          layout="dialog"
          activeTab={props.activePanel}
          type={panel.type}
          onSubmit={onSubmit}
          onOriginChange={props.onOriginChange}
          onAirlineChange={props.onAirlineChange}
          onDestinationChange={props.onDestinationChange}
          onDepartureDateChange={props.onDepartureDateChange}
          onJourneyTypeChange={props.onJourneyTypeChange}
          onReturnDateChange={props.onReturnDateChange}
          onCurrentPopupDataChange={(data) => {
            setCurrentPopupData(data);
          }}
          fetchAirports={props.fetchAirports}
          filterFields={props.filterFields}
          fetchBlackoutDates={props.fetchBlackoutDates}
          defaultOrigin={popupData.origin}
          defaultDestination={popupData.destination}
          rooms={props.businessRules.rooms}
          passengers={props.businessRules.travelers}
          passengersRules={props.passengersRules}
          additionalRules={rules}
          travelClasses={businessRules.travelClasses}
          journeyTypes={journeyTypes}
          dropOff={dropOff}
          carTypes={carTypes}
          labels={panel.labels ?? {}}
          defaultDates={dates}
          componentsConfigurations={configConfiguration}
          overrideComponentsConfigurations={panel}
          hotelCategories={hotelCategories}
          blackoutDates={panel.blackoutDates}
          maxTripDuration={panel.maxTripDuration}
          minTripDuration={panel.minTripDuration}
          ages={driverAges}
          nationalities={nationalities}
          airlines={airlinesSelector}
          fetchAirlines={props.fetchAirlines}
          showAdvancedFilter={panel.showAdvancedFilter}
          prepopulatedAirlines={prepopulatedAirlines}
          originDestinationLogic={props.originDestinationLogic}
          businessRules={props.businessRules}
          disclaimer={disclaimer}
          isMobile={isMobile}
          preventBottomDisplacement={preventBottomDisplacement}
          allowHoverStyleInRange={props.componentsConfiguration?.allowHoverStyleInRange}
          enableLegend={panel.enableLegend}
        />
      </PanelComponent>
    );
  });

  let panel: React.ReactElement | React.ReactElement[] = panelsComponent;
  if (!isOnePanel) {
    panel = (
      <Tabs
        onChange={(index) => {
          props?.setActivePanel?.(index);
        }}
        sx={{
          '[data-reach-tab]': {
            variant: 'buttons.group'
          },
          '[data-selected]': {
            variant: 'buttons.groupSelected'
          },
          height: height
        }}
      >
        <TabList>
          <Inline gap="2px" sx={{ maxWidth: 'tabListWidth' }}>
            {panels.length > 1 &&
              panels.map((panel) => (
                <BookingTab
                  key={panel.name}
                  labelKey={panel.label}
                  defaultLabel={panel.label}
                  icon={panel.icon}
                />
              ))}
          </Inline>
        </TabList>
        <TabPanels
          sx={isMobile ? { pt: '5px', height: 'calc(100% - 68px)!important' } : { pt: '5px' }}
        >
          {panelsComponent}
        </TabPanels>
      </Tabs>
    );
  }

  if (isMobile) {
    return (
      <MobileFilterDialog label={translate('cmp-booking-popup-dialog-title', 'Booking pop-up')}>
        <Inline
          align="right"
          pt="3"
          pr="3"
          sx={{ variant: 'layout.popup.mobileCloseHeader' }}
          className="MobileCloseHeader"
        >
          <Button
            variant="icon"
            onClick={() => close(currentPopupData)}
            aria-label={translate('cmp-booking-popup-close-button', 'close')}
            ref={buttonRef}
          >
            <Icon icon="close" size={20} sx={{ stroke: 'primary', strokeWidth: '70' }} />
          </Button>
        </Inline>
        <MobileBody>
          <Stack
            gap="4"
            pl="3"
            pr="3"
            pt="4"
            pb="4"
            style={isMobile ? { height: '100%' } : undefined}
          >
            <Heading color="text">
              {translate('cmp-booking-popup-title', 'Title of pop-up')}
            </Heading>
            {panel}
          </Stack>
        </MobileBody>
      </MobileFilterDialog>
    );
  }
  return (
    <PopupDialog
      label={translate('cmp-booking-popup-label', 'Booking pop-up')}
      title={translate('cmp-booking-popup-title', 'Title of pop-up')}
      onDismiss={() => close(currentPopupData)}
    >
      {panel}
    </PopupDialog>
  );
}

export const BookingPopupContainer = withContainer(BookingPopup, bookingStyles);
